'use client';

import type {
  ModalWalkthroughStep,
  TooltipWalkthroughStep,
} from '@unifyapps/defs/types/page/walkthrough';
import { useWalkthroughManagerStore } from '../../../../stores/WalkthroughManagerStore';
import { useWalkthroughStepVisibility } from '../../../hooks/useWalkthroughStepVisibility';
import { WalkthroughTooltipStepCore } from '../components/WalkthroughTooltipStepCore';
import { WalkthroughModalDisplay } from '../displays/WalkthroughModalDisplay';
import { WalkthroughTooltipDisplay } from '../displays/WalkthroughTooltipDisplay';

/**
 * TabletWalkthroughStep - Tablet-specific walkthrough step component.
 * Routes to appropriate display component based on displayType.
 * Can be extended with tablet-specific adjustments.
 * Uses activeStep for element finding and displayStep for UI rendering during transitions.
 */
export function TabletWalkthroughStep({ handleMissingTargetElement }: { handleMissingTargetElement?: () => void }) {
  const activeStep = useWalkthroughManagerStore().hooks.useActiveStep();
  const displayStep = useWalkthroughManagerStore().hooks.useDisplayStep();
  const displayIndex = useWalkthroughManagerStore().hooks.useDisplayStepIndex();
  const transitionState = useWalkthroughManagerStore().hooks.useTooltipTransitionState();
  const shouldShowStep = useWalkthroughStepVisibility(displayStep);

  if (displayIndex === undefined || !displayStep || !shouldShowStep || !activeStep) {
    return null;
  }

  // Check if we're in transition using transition state machine status
  // During transition, hide tooltip content until new element is ready
  const isTransitioning = (transitionState?.status ?? 'idle') !== 'idle';

  // Type narrowing based on displayType
  // Use activeStep for element finding (the step we're transitioning to)
  // Use displayStep for UI rendering (the step currently shown)
  if (activeStep.displayType === 'tooltip') {
    const activeTooltipStep = activeStep as TooltipWalkthroughStep;
    const displayTooltipStep = displayStep as TooltipWalkthroughStep;
    return (
      <WalkthroughTooltipStepCore step={activeTooltipStep} handleMissingTargetElement={handleMissingTargetElement}>
        {({ targetElement, animationState, handleResize }) => {
          if (!targetElement) {
            return null;
          }

          // Hide tooltip content during transition until:
          // 1. displayIndex catches up to activeIndex (isTransitioning === false)
          // 2. AND highlight overlay animation completes (animationState.isAnimating === false)
          if (isTransitioning || animationState.isAnimating) {
            return null;
          }

          return (
            <WalkthroughTooltipDisplay
              animationState={animationState}
              onUpdate={handleResize}
              step={displayTooltipStep}
              targetElement={targetElement}
            />
          );
        }}
      </WalkthroughTooltipStepCore>
    );
  }

  if (activeStep.displayType === 'modal') {
    const modalStep = activeStep as ModalWalkthroughStep;
    return <WalkthroughModalDisplay step={modalStep} />;
  }

  return null;
}
